import { useConfig } from "@mittwald/ext-bridge/react";
import {
  Button,
  Heading,
  Section,
  Text,
  Header,
  AccentBox,
  Link,
  Icon,
  Alert,
  Content,
} from "@mittwald/flow-remote-react-components";
import { useCreateSession } from "../hooks/useCreateSession.ts";
import { useVariantStatus } from "../hooks/useVariantStatus.ts";
import { IconPointerExclamation } from "@tabler/icons-react";
import { useChangeVariantProject } from "../hooks/useChangeVariantProject.ts";

export const WebSSH = () => {
  const config = useConfig();
  const projectId = config.projectId ?? "";

  const containerId = config.containerId;

  const target = containerId
    ? { containerId }
    : { appInstallationId: config.appInstallationId ?? "" };

  const createSessionMutation = useCreateSession(projectId, target);
  const variantStatus = useVariantStatus(projectId);

  const changeProjectMutation = useChangeVariantProject(projectId);

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes("edg/")) {
      return {
        name: "Edge",
        link: "https://support.microsoft.com/de-de/microsoft-edge/blockierte-popups-in-microsoft-edge-zulassen-1d8ba4f8-f385-9a0b-e944-aa47339b6bb5",
      };
    } else if (userAgent.includes("chrome") && !userAgent.includes("edg/")) {
      return {
        name: "Chrome",
        link: "https://support.google.com/chrome/answer/95472?hl=de",
      };
    } else if (userAgent.includes("firefox")) {
      return {
        name: "Firefox",
        link: "https://support.mozilla.org/de/kb/pop-blocker-einstellungen-ausnahmen-problemloesung#w_einstellungen-des-pop-up-blockers",
      };
    } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
      return {
        name: "Safari",
        link: "https://support.apple.com/de-de/102524",
      };
    } else if (userAgent.includes("opr/") || userAgent.includes("opera")) {
      return {
        name: "Opera",
        link: "https://help.opera.com/de/latest/web-preferences/#popUps",
      };
    }

    return null;
  };

  const handleButtonClick = async () => {
    const session = await createSessionMutation.mutateAsync();

    if (session.error) {
      throw new Error(
        `Error while creating SSH session: ${session.error.message}`,
      );
    }

    const url = new URL(window.location.href);
    const urlToOpen = `${url}terminal/${session.data.sessionId}`;

    window.open(urlToOpen, "_blank");
  };

  const browserInfo = getBrowserInfo();

  const variant = variantStatus.data;

  // Blocked: free variant, project not allowed
  if (variant?.variantKey === "free" && !variant.allowed) {
    const nextChangeDate = variant.nextChangeAvailableAt
      ? new Date(variant.nextChangeAvailableAt).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : undefined;

    return (
      <Section>
        <Header>
          <Heading>Web-SSH</Heading>
        </Header>
        <Alert status="danger">
          <Heading>Kostenloser Zugang eingeschränkt</Heading>
          <Content>
            <Text>
              Dein kostenloser Zugang ist aktuell an das Projekt
              {variant.freeProjectDescription
                ? ` "${variant.freeProjectDescription}"`
                : " ein anderes Projekt"}{" "}
              gebunden.
              {!variant.canChangeProject &&
                nextChangeDate &&
                ` Nächster Wechsel möglich am ${nextChangeDate}.`}
            </Text>
            {variant.canChangeProject && (
              <Button
                onPress={() => void changeProjectMutation.mutateAsync()}
                isPending={changeProjectMutation.isPending}
              >
                Zu diesem Projekt wechseln
              </Button>
            )}
            <Button
              color="success"
              onPress={() =>
                window.open(
                  `https://studio.mittwald.de/app/organizations/${config.customerId ?? ""}/extensions/${config.extensionInstanceId}`,
                  "_blank",
                )
              }
            >
              Upgrade auf Unlimited
            </Button>
          </Content>
        </Alert>
        <Text>
          Mit dem Unlimited-Abo (5 €/Monat) kannst du Web-SSH in allen deinen
          Projekten nutzen – ohne Einschränkung.
        </Text>
      </Section>
    );
  }

  // Allowed (unlimited, free with correct project, or loading)
  return (
    <Section>
      <Header>
        <Heading>Web-SSH</Heading>
        <Button
          color="success"
          onPress={() => void handleButtonClick()}
          isPending={createSessionMutation.isPending}
        >
          Terminal öffnen
        </Button>
      </Header>
      <Text>
        Öffne ein sicheres Terminal direkt im Browser. So verwaltest du dein
        Projekt per SSH – ohne lokalen Client. Ideal für schnelle Wartung: Logs
        prüfen, Git/Composer, WP-CLI, npm & mehr.
      </Text>
      <AccentBox>
        <Icon>
          <IconPointerExclamation />
        </Icon>
        <Section>
          <Heading>Hinweise zur Nutzung</Heading>
          <Text>
            Die Konsole öffnet sich beim Klick auf &ldquo;Terminal öffnen&rdquo;
            in einem neuen Fenster. Sollte das nicht passieren, erlaube bitte
            Pop-Ups in deinem Browser.
          </Text>
          {browserInfo && (
            <>
              {" "}
              <Link href={browserInfo.link} target="_blank">
                Anleitung für {browserInfo.name} öffnen
              </Link>
            </>
          )}
        </Section>
      </AccentBox>
    </Section>
  );
};
